import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/ui/skeleton";
import { TableCell, TableRow } from "@/shared/ui/table";

/**
 * Repeated full-width rows — use for lists, tables, and card grids.
 *
 * @param count  Number of skeleton rows (default 4)
 * @param height Tailwind height class per row (default "h-12")
 * @param radius Tailwind border-radius class (default "rounded-xl")
 * @param gap    Tailwind gap/space-y class (default "space-y-2")
 */
export function ListSkeleton(t0) {
  const $ = _c(13);
  const {
    count: t1,
    height: t2,
    radius: t3,
    gap: t4,
    className
  } = t0;
  const count = t1 === undefined ? 4 : t1;
  const height = t2 === undefined ? "h-12" : t2;
  const radius = t3 === undefined ? "rounded-xl" : t3;
  const gap = t4 === undefined ? "space-y-2" : t4;
  let t5;
  if ($[0] !== className || $[1] !== gap) {
    t5 = cn(gap, className);
    $[0] = className;
    $[1] = gap;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== count || $[4] !== height || $[5] !== radius) {
    let t7;
    if ($[7] !== height || $[8] !== radius) {
      t7 = (_, i) => <Skeleton key={i} className={cn(height, "w-full", radius)} />;
      $[7] = height;
      $[8] = radius;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    t6 = Array.from({
      length: count
    }, t7);
    $[3] = count;
    $[4] = height;
    $[5] = radius;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}

/**
 * Single large block — use for charts and data visualizations.
 *
 * @param height Tailwind height class (default "h-48")
 */
export function ChartSkeleton(t0) {
  const $ = _c(5);
  const {
    height: t1,
    className
  } = t0;
  const height = t1 === undefined ? "h-48" : t1;
  let t2;
  if ($[0] !== className || $[1] !== height) {
    t2 = cn(height, "w-full rounded-lg", className);
    $[0] = className;
    $[1] = height;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <Skeleton className={t2} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * Row of pill-shaped badges — use for tag/chip groups and metadata rows.
 *
 * @param widths Array of Tailwind width classes for each badge
 */
export function BadgeRowSkeleton(t0) {
  const $ = _c(10);
  const {
    widths,
    height: t1,
    className
  } = t0;
  const height = t1 === undefined ? "h-6" : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex flex-wrap gap-2", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== height || $[3] !== widths) {
    let t4;
    if ($[5] !== height) {
      t4 = (w, i) => <Skeleton key={i} className={cn(height, w, "rounded-full")} />;
      $[5] = height;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = widths.map(t4);
    $[2] = height;
    $[3] = widths;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export const DEFAULT_TABLE_SKELETON_COLUMNS = 6;
export const DEFAULT_TABLE_SKELETON_ROWS = 12;
export type TableContentSkeletonColumn = {
  header?: React.ReactNode;
  width?: number;
  minWidth?: number;
  flex?: number;
};
export type TableContentSkeletonProps = {
  rowCount?: number;
  columnCount?: number;
  headers?: React.ReactNode[];
  columns?: TableContentSkeletonColumn[];
  showFooter?: boolean;
  className?: string;
};
const TABLE_SKELETON_ROW_CLASS = "border-b transition-colors hover:bg-transparent";
const TABLE_SKELETON_CELL_CLASS = "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0";
const TABLE_SKELETON_BAR_WIDTHS = ["w-4/5", "w-1/2", "w-2/3"] as const;
function getSkeletonBarWidth(index: number) {
  return TABLE_SKELETON_BAR_WIDTHS[index % TABLE_SKELETON_BAR_WIDTHS.length];
}
function getDefaultGridTrack(index: number) {
  return index === 0 ? "minmax(12rem, 1.4fr)" : "minmax(7rem, 1fr)";
}
function toPixelTrack(value: number) {
  return `${Math.max(1, value)}px`;
}
function getColumnGridTrack(column: TableContentSkeletonColumn | undefined, index: number) {
  if (!column) return getDefaultGridTrack(index);
  const width = typeof column.width === "number" && Number.isFinite(column.width) ? column.width : undefined;
  const minWidth = typeof column.minWidth === "number" && Number.isFinite(column.minWidth) ? column.minWidth : undefined;
  const flex = typeof column.flex === "number" && Number.isFinite(column.flex) && column.flex > 0 ? column.flex : undefined;
  if (flex) return `minmax(${toPixelTrack(minWidth ?? width ?? (index === 0 ? 192 : 112))}, ${flex}fr)`;
  if (width) return toPixelTrack(width);
  if (minWidth) return `minmax(${toPixelTrack(minWidth)}, 1fr)`;
  return getDefaultGridTrack(index);
}
export const TableContentSkeleton = React.forwardRef<HTMLDivElement, TableContentSkeletonProps>((t0, ref) => {
  const $ = _c(32);
  const {
    rowCount: t1,
    columnCount,
    headers,
    columns,
    showFooter: t2,
    className
  } = t0;
  const rowCount = t1 === undefined ? DEFAULT_TABLE_SKELETON_ROWS : t1;
  const showFooter = t2 === undefined ? false : t2;
  const safeColumnCount = Math.max(1, columnCount ?? columns?.length ?? headers?.length ?? DEFAULT_TABLE_SKELETON_COLUMNS);
  let t3;
  if ($[0] !== columns || $[1] !== safeColumnCount) {
    let t4;
    if ($[3] !== columns) {
      t4 = (_, index) => getColumnGridTrack(columns?.[index], index);
      $[3] = columns;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    t3 = Array.from({
      length: safeColumnCount
    }, t4);
    $[0] = columns;
    $[1] = safeColumnCount;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const gridTemplateColumns = t3.join(" ");
  let t4;
  if ($[5] !== className) {
    t4 = cn("bg-card ring-foreground/8 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg shadow-xs ring-1", className);
    $[5] = className;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== gridTemplateColumns) {
    t5 = {
      gridTemplateColumns
    };
    $[7] = gridTemplateColumns;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== safeColumnCount) {
    t6 = Array.from({
      length: safeColumnCount
    });
    $[9] = safeColumnCount;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== columns || $[12] !== headers || $[13] !== t6) {
    t7 = t6.map((__0, index_0) => <div key={index_0} className="truncate">{columns?.[index_0]?.header ?? headers?.[index_0] ?? null}</div>);
    $[11] = columns;
    $[12] = headers;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div className="border-foreground/8 text-foreground grid h-10 items-center gap-4 border-b px-4 text-xs font-bold" style={t5}>{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== rowCount) {
    t9 = Array.from({
      length: rowCount
    });
    $[18] = rowCount;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== gridTemplateColumns || $[21] !== safeColumnCount || $[22] !== t9) {
    t10 = <div className="divide-foreground/8 min-h-0 flex-1 divide-y overflow-hidden">{t9.map((__1, rowIndex) => <div key={rowIndex} className="grid h-11 items-center gap-4 px-4" style={{
        gridTemplateColumns
      }}>{Array.from({
          length: safeColumnCount
        }).map(_temp)}</div>)}</div>;
    $[20] = gridTemplateColumns;
    $[21] = safeColumnCount;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== showFooter) {
    t11 = showFooter ? <div className="border-foreground/8 flex min-h-10 items-center justify-between border-t px-3 py-1"><div className="bg-muted h-3 w-32 animate-pulse rounded-full" /><div className="bg-muted h-3 w-48 animate-pulse rounded-full" /></div> : null;
    $[24] = showFooter;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== ref || $[27] !== t10 || $[28] !== t11 || $[29] !== t4 || $[30] !== t8) {
    t12 = <div ref={ref} aria-busy="true" aria-live="polite" data-testid="table-content-skeleton" className={t4}>{t8}{t10}{t11}</div>;
    $[26] = ref;
    $[27] = t10;
    $[28] = t11;
    $[29] = t4;
    $[30] = t8;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
});
TableContentSkeleton.displayName = "TableContentSkeleton";
export function TableSkeletonRows(t0) {
  const $ = _c(5);
  const {
    columnCount: t1,
    rowCount: t2
  } = t0;
  const columnCount = t1 === undefined ? DEFAULT_TABLE_SKELETON_COLUMNS : t1;
  const rowCount = t2 === undefined ? 3 : t2;
  const safeColumnCount = Math.max(1, columnCount);
  let t3;
  if ($[0] !== rowCount) {
    t3 = Array.from({
      length: rowCount
    });
    $[0] = rowCount;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== safeColumnCount || $[3] !== t3) {
    t4 = <>{t3.map((_, rowIndex) => <TableRow key={rowIndex} aria-hidden="true" data-testid="table-skeleton-row" className={TABLE_SKELETON_ROW_CLASS}>{Array.from({
          length: safeColumnCount
        }).map(_temp2)}</TableRow>)}</>;
    $[2] = safeColumnCount;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function _temp2(__0, cellIndex) {
  return <TableCell key={cellIndex} className={TABLE_SKELETON_CELL_CLASS}><Skeleton className={cn("h-4 rounded-full", getSkeletonBarWidth(cellIndex))} /></TableCell>;
}
function _temp(__2, cellIndex) {
  return <div key={cellIndex} className={cn("bg-muted/80 h-3 animate-pulse rounded-full", getSkeletonBarWidth(cellIndex))} />;
}